import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { useSaveAscertain } from "@features/cart/hook/useSaveAscertain";
import { IEirSalesAscertain } from "@modules/eirSalesFacade/interfaces/models/IEirSalesAscertain";
import { ICart } from "@features/cart/ICart";
import { mapEirSalesSaveAscertainPayload } from "@modules/eirSalesFacade/mappers/mapEirSalesSaveAscertainPayload";
import { EYesNoNoneValues } from "@interface/EYesNoNoneValues";
import { useTranslation } from "react-i18next";

interface IParams {
    offerId: number;
    setCart: Dispatch<SetStateAction<ICart | undefined>>;
    ascertain?: IEirSalesAscertain;
    isAscertainLoading?: boolean;
    onValidationChange(errorMessage: string | undefined): void;
    radioValue?: EYesNoNoneValues;
    setRadioValue?: Dispatch<SetStateAction<EYesNoNoneValues>>;
    onAgreeConfirmed?(): void;
}

export const useBroadbandProviderQuestion = (props: IParams) => {
    const {
        offerId,
        setCart,
        ascertain,
        isAscertainLoading = false,
        onValidationChange,
        radioValue: externalRadioValue,
        setRadioValue: setExternalRadioValue,
        onAgreeConfirmed,
    } = props;
    const { t } = useTranslation("cart");
    const [internalRadioValue, setInternalRadioValue] = useState<EYesNoNoneValues>(EYesNoNoneValues.NONE);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const { saveAscertain, isLoading: isSaveAscertainLoading } = useSaveAscertain({ offerId, setCart });

    const isLoading = isAscertainLoading || isSaveAscertainLoading;
    const radioValue = externalRadioValue ?? internalRadioValue;
    const setRadioValue = setExternalRadioValue ?? setInternalRadioValue;

    const openAccordion = useCallback(() => setIsAccordionOpen(true), []);
    const closeAccordion = useCallback(() => setIsAccordionOpen(false), []);

    useEffect(() => {
        if (isAscertainLoading) {
            return;
        }

        // If the user already answered (radioValue !== NONE), do not override their choice with ascertain.
        // Ascertain is persisted backend state; until "Agree and continue" is clicked for YES, the UI choice should remain.
        if (radioValue !== EYesNoNoneValues.NONE) {
            return;
        }

        if (ascertain) {
            if (ascertain.hasActiveBroadband) {
                setRadioValue(EYesNoNoneValues.YES);
                closeAccordion();
            } else {
                setRadioValue(EYesNoNoneValues.NO);
                closeAccordion();
            }
        }
    }, [ascertain, closeAccordion, isAscertainLoading, radioValue, setRadioValue]);

    const handleAgreeAndContinue = useCallback(async () => {
        const payload = mapEirSalesSaveAscertainPayload(true);
        await saveAscertain(payload, () => {
            closeAccordion();
            onValidationChange(undefined);
            onAgreeConfirmed?.();
        });
    }, [saveAscertain, closeAccordion, onAgreeConfirmed, onValidationChange]);

    const handleNoSelected = useCallback(async () => {
        const payload = mapEirSalesSaveAscertainPayload(false);
        await saveAscertain(payload, closeAccordion);
    }, [saveAscertain, closeAccordion]);

    const handleRadioChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            onValidationChange(undefined);
            const value = event.target.value as EYesNoNoneValues.YES | EYesNoNoneValues.NO;
            setRadioValue(value);

            if (value === EYesNoNoneValues.YES) {
                openAccordion();
            } else {
                closeAccordion();
                await handleNoSelected();
            }
        },
        [onValidationChange, setRadioValue, openAccordion, closeAccordion, handleNoSelected]
    );

    const getIsValid = useCallback(() => {
        if (radioValue === EYesNoNoneValues.NONE) {
            return false;
        }

        if (radioValue === EYesNoNoneValues.YES) {
            return !isAccordionOpen || Boolean(ascertain?.hasActiveBroadband);
        }

        return true;
    }, [radioValue, isAccordionOpen, ascertain]);

    const getValidationErrorMessage = useCallback(() => {
        if (radioValue === EYesNoNoneValues.NONE) {
            return t("broadband.noOptionSelectedValidationError");
        }

        if (
            radioValue === EYesNoNoneValues.YES &&
            isAccordionOpen &&
            (ascertain === undefined || !ascertain.hasActiveBroadband)
        ) {
            return t("broadband.agreeValidationError");
        }

        return undefined;
    }, [radioValue, t, isAccordionOpen, ascertain]);

    return {
        radioValue,
        isAccordionOpen,
        isLoading,
        handleRadioChange,
        handleAgreeAndContinue,
        getIsValid,
        getValidationErrorMessage,
    };
};
